import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { reportSorterToOrdering } from 'services/utils.ts';
import type {
  ReportListItem,
  ReportListParams,
  DatePreset,
} from '@/models/report';
import type { TableProps } from 'antd';

export type ReportTab = 'All' | 'Draft' | 'Preliminary' | 'Final';

const TAB_TO_STATUS: Record<ReportTab, string | undefined> = {
  All: undefined,
  Draft: 'DRAFT',
  Preliminary: 'PRELIMINARY',
  Final: 'FINAL',
};

function resolveDateRange(
  preset: DatePreset,
  custom: [string, string] | null
): { updated_date_from?: string; updated_date_to?: string } {
  const today = moment().format('YYYY-MM-DD');
  switch (preset) {
    case 'today':
      return { updated_date_from: today, updated_date_to: today };
    case 'last7':
      return {
        updated_date_from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        updated_date_to: today,
      };
    case 'last30':
      return {
        updated_date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        updated_date_to: today,
      };
    case 'custom':
      if (!custom) return {};
      return { updated_date_from: custom[0], updated_date_to: custom[1] };
    default:
      return {};
  }
}

export function useReportListFilters() {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);

  const [activeTab, setActiveTab] = useState<ReportTab>('All');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedStudyTypes, setSelectedStudyTypes] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('last7');
  const [customDateRange, setCustomDateRange] = useState<
    [string, string] | null
  >(null);

  const [selectedReferringPhysicians, setSelectedReferringPhysicians] =
    useState<string[]>([]);
  const [selectedInterpretingPhysicians, setSelectedInterpretingPhysicians] =
    useState<string[]>([]);
  const [selectedSonographers, setSelectedSonographers] = useState<string[]>(
    []
  );

  // ---- debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 350);
    return () => clearTimeout(id);
  }, [searchText]);

  // ---- derived flags
  const hasActiveFilters = useMemo(
    () =>
      selectedSites.length > 0 ||
      selectedStudyTypes.length > 0 ||
      datePreset !== 'last7' ||
      selectedReferringPhysicians.length > 0 ||
      selectedInterpretingPhysicians.length > 0 ||
      selectedSonographers.length > 0,
    [
      selectedSites,
      selectedStudyTypes,
      datePreset,
      selectedReferringPhysicians,
      selectedInterpretingPhysicians,
      selectedSonographers,
    ]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedSites.length > 0) count += 1;
    if (selectedStudyTypes.length > 0) count += 1;
    if (datePreset !== 'last7') count += 1;
    if (selectedReferringPhysicians.length > 0) count += 1;
    if (selectedInterpretingPhysicians.length > 0) count += 1;
    if (selectedSonographers.length > 0) count += 1;
    return count;
  }, [
    selectedSites,
    selectedStudyTypes,
    datePreset,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
  ]);

  const hasActiveSearch = debouncedSearch.length > 0;

  // ---- assembled params (for table data — includes status)
  const params = useMemo<ReportListParams>(() => {
    const dateRange = resolveDateRange(datePreset, customDateRange);
    return {
      page,
      size,
      ordering,
      status: TAB_TO_STATUS[activeTab],
      search: debouncedSearch || undefined,
      site: selectedSites.length > 0 ? selectedSites : undefined,
      study_type:
        selectedStudyTypes.length > 0 ? selectedStudyTypes : undefined,
      ...dateRange,
      referring_physician:
        selectedReferringPhysicians.length > 0
          ? selectedReferringPhysicians
          : undefined,
      interpreting_physician:
        selectedInterpretingPhysicians.length > 0
          ? selectedInterpretingPhysicians
          : undefined,
      sonographer:
        selectedSonographers.length > 0 ? selectedSonographers : undefined,
    };
  }, [
    page,
    size,
    ordering,
    activeTab,
    debouncedSearch,
    selectedSites,
    selectedStudyTypes,
    datePreset,
    customDateRange,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
  ]);

  // ---- counts params (no status — stable tab badge counts)
  const countsParams = useMemo<ReportListParams>(() => {
    const dateRange = resolveDateRange(datePreset, customDateRange);
    return {
      page: 1,
      size: 1,
      search: debouncedSearch || undefined,
      site: selectedSites.length > 0 ? selectedSites : undefined,
      study_type:
        selectedStudyTypes.length > 0 ? selectedStudyTypes : undefined,
      ...dateRange,
      referring_physician:
        selectedReferringPhysicians.length > 0
          ? selectedReferringPhysicians
          : undefined,
      interpreting_physician:
        selectedInterpretingPhysicians.length > 0
          ? selectedInterpretingPhysicians
          : undefined,
      sonographer:
        selectedSonographers.length > 0 ? selectedSonographers : undefined,
    };
  }, [
    debouncedSearch,
    selectedSites,
    selectedStudyTypes,
    datePreset,
    customDateRange,
    selectedReferringPhysicians,
    selectedInterpretingPhysicians,
    selectedSonographers,
  ]);

  // ---- callbacks
  const onTabChange = (tab: ReportTab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const onTableChange: TableProps<ReportListItem>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    setPage(pagination?.current ?? page);
    setSize(pagination?.pageSize ?? size);
    setOrdering(reportSorterToOrdering(sorter));
  };

  const clearFilters = () => {
    setSelectedSites([]);
    setSelectedStudyTypes([]);
    setDatePreset('last7');
    setCustomDateRange(null);
    setSelectedReferringPhysicians([]);
    setSelectedInterpretingPhysicians([]);
    setSelectedSonographers([]);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchText('');
    setDebouncedSearch('');
    setPage(1);
  };

  return {
    params,
    countsParams,
    activeTab,
    onTabChange,
    searchText,
    setSearchText,
    selectedSites,
    onSitesChange: setSelectedSites,
    selectedStudyTypes,
    onStudyTypesChange: setSelectedStudyTypes,
    datePreset,
    onDatePresetChange: (preset: DatePreset) => {
      setDatePreset(preset);
      if (preset !== 'custom') setCustomDateRange(null);
      setPage(1);
    },
    customDateRange,
    onCustomDateRangeChange: setCustomDateRange,
    selectedReferringPhysicians,
    onReferringPhysiciansChange: setSelectedReferringPhysicians,
    selectedInterpretingPhysicians,
    onInterpretingPhysiciansChange: setSelectedInterpretingPhysicians,
    selectedSonographers,
    onSonographersChange: setSelectedSonographers,
    hasActiveFilters,
    activeFilterCount,
    hasActiveSearch,
    clearFilters,
    clearSearch,
    onTableChange,
  };
}
